/**
 * Screen 1a — the learner's home.
 *
 * Assigned work on top, browsable shelves below. That order is the whole point
 * of the screen: what somebody has been given is the thing they came for, and
 * the catalogue is what they do afterwards.
 *
 * One request. The three shelves share the same resolved assignment set, and
 * three requests would resolve it three times and could disagree about which
 * courses are overdue.
 */

import { useEffect } from 'react';
import { learn } from '../api';
import { AssignedRow, BundleTile, CourseTile, TileGrid } from '../components/Bits';
import { Empty, ErrorNote, Loading, PageTitle, Panel, PanelHead } from '../components/Chrome';
import { setAssignedCount } from '../counts';
import { ds } from '../ds';
import { useRouter } from '../router';
import { useApp } from '../state';
import { meta, plural } from '../theme';
import { useLoad } from '../useLoad';

export default function Home() {
  const { Button } = ds();
  const { navigate } = useRouter();
  const { me } = useApp();
  const { data, error, loading } = useLoad(() => learn.home(), 'Could not load your learning.');

  // The rail's count, published rather than fetched again — see counts.ts.
  useEffect(() => {
    if (data) setAssignedCount(data.counts.assigned);
  }, [data]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  const { counts, assigned, bundles, newInCompany } = data;

  return (
    <>
      <PageTitle
        title="Your learning."
        meta={meta(
          `${counts.assigned} assigned`,
          counts.overdue > 0 ? `${counts.overdue} overdue` : null,
          `${counts.finishedThisMonth} finished this month`
        )}
      />

      <Panel style={{ display: 'grid', gap: 4 }}>
        <PanelHead title="Assigned to you">
          <Button variant="ghost" size="sm" onClick={() => navigate('/learning')}>
            See all
          </Button>
        </PanelHead>

        {assigned.length === 0 ? (
          <p style={{ margin: '10px 0 0', fontSize: 14, color: 'var(--text-muted)' }}>
            Nothing is assigned to you.
          </p>
        ) : (
          assigned.map((item) => <AssignedRow key={item.course.id} item={item} />)
        )}
      </Panel>

      {bundles.length > 0 && (
        <Panel style={{ display: 'grid', gap: 16 }}>
          {/* The design heads this "Bundles for <team>". `GET /me` carries no
              team name — see the report accompanying this pass — so the shelf
              is headed for the reader instead of for a word we do not have. */}
          <PanelHead title="Bundles for you" meta={plural(bundles.length, 'bundle')} />
          <TileGrid>
            {bundles.map((b) => (
              <BundleTile key={b.id} bundle={b} />
            ))}
          </TileGrid>
        </Panel>
      )}

      {/* A guest is not in the company: their home is what they were invited to. */}
      {!me?.external && (
        <Panel style={{ display: 'grid', gap: 16 }}>
          <PanelHead title="New in your company">
            <Button variant="ghost" size="sm" onClick={() => navigate('/catalogue')}>
              Browse catalogue
            </Button>
          </PanelHead>

          {newInCompany.length === 0 ? (
            <Empty line="No course has been published for everyone yet.">
              <Button variant="secondary" size="sm" onClick={() => navigate('/catalogue')}>
                Browse catalogue
              </Button>
            </Empty>
          ) : (
            <TileGrid>
              {newInCompany.map((c) => (
                <CourseTile key={c.id} course={c} />
              ))}
            </TileGrid>
          )}
        </Panel>
      )}
    </>
  );
}
